import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { weeklyPlan } from '@/data/index';
import { DayCard, MealCard, SummaryStrip } from '@/components/MealComponents';

export default function Home() {
  const [selectedDay, setSelectedDay] = useState(0);

  const currentPlan = weeklyPlan[selectedDay];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center text-xl">
              🏋️
            </div>
            <div>
              <h1 className="text-lg font-bold text-foreground">GYM Coach</h1>
              <p className="text-xs text-muted-foreground">Weekly Nutrition Plan</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-400/10 border border-green-400/30">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-xs text-green-400 font-medium">Active Plan</span>
            </div>
            <div className="px-3 py-1.5 rounded-full bg-primary/10 border border-primary/30">
              <span className="text-xs text-primary font-bold">7 Days</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Hero section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 40 }}
          className="mb-10 text-center"
        >
          <h2 className="text-4xl sm:text-5xl font-black tracking-tight mb-3">
            <span className="text-primary">Weekly</span>{' '}
            <span className="text-foreground">Meal Plan</span>
          </h2>
          <p className="text-muted-foreground text-sm sm:text-base max-w-xl mx-auto">
            Professional nutrition schedule designed for peak athletic performance.
            Each day is carefully planned to fuel your workouts and recovery.
          </p>
        </motion.div>

        {/* Day Selector */}
        <div className="mb-8">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-4">
            Select Day
          </h3>
          <div className="grid grid-cols-7 gap-2">
            {weeklyPlan.map((plan, index) => (
              <DayCard
                key={plan.day}
                plan={plan}
                index={index}
                isSelected={selectedDay === index}
                onClick={() => setSelectedDay(index)}
              />
            ))}
          </div>
        </div>

        {/* Selected Day Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedDay}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            {/* Day Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-black text-foreground">
                  {currentPlan.day}
                </h2>
                <p className="text-muted-foreground text-sm mt-0.5">
                  {currentPlan.dayAr} — {currentPlan.meals.length} meals planned
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelectedDay(Math.max(0, selectedDay - 1))}
                  disabled={selectedDay === 0}
                  className="w-8 h-8 rounded-lg bg-muted border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/50 disabled:opacity-30 transition-all"
                >
                  ‹
                </button>
                <button
                  onClick={() => setSelectedDay(Math.min(weeklyPlan.length - 1, selectedDay + 1))}
                  disabled={selectedDay === weeklyPlan.length - 1}
                  className="w-8 h-8 rounded-lg bg-muted border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/50 disabled:opacity-30 transition-all"
                >
                  ›
                </button>
              </div>
            </div>

            {/* Summary Strip */}
            <SummaryStrip plan={currentPlan} />

            {/* Meals Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {currentPlan.meals.map((meal, index) => (
                <MealCard key={`${meal.type}-${index}`} meal={meal} index={index} />
              ))}
            </div>

            {/* Legend */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-8 p-4 rounded-2xl border border-border bg-card"
            >
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">
                Color Legend
              </h4>
              <div className="flex flex-wrap gap-3">
                {[
                  { color: 'bg-amber-400', label: 'Breakfast' },
                  { color: 'bg-orange-400', label: 'Pre-Workout' },
                  { color: 'bg-green-400', label: 'Post-Workout' },
                  { color: 'bg-purple-400', label: 'Snack' },
                  { color: 'bg-blue-400', label: 'Dinner' },
                  { color: 'bg-cyan-400', label: 'Meal 2' },
                  { color: 'bg-teal-400', label: 'Meal 3' },
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${item.color}`} />
                    <span className="text-xs text-muted-foreground">{item.label}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Full Week Overview */}
      <section className="max-w-7xl mx-auto px-4 pb-12 mt-6">
        <div className="border-t border-border pt-10">
          <h3 className="text-xl font-black mb-6 text-foreground">Full Week Overview</h3>
          <div className="overflow-x-auto rounded-2xl border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-28">
                    Day
                  </th>
                  {['Breakfast', 'Pre-Workout', 'Post-Workout', 'Snack', 'Dinner / Other'].map(col => (
                    <th key={col} className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {weeklyPlan.map((plan, dayIdx) => {
                  const breakfast = plan.meals.find(m => m.type === 'breakfast');
                  const preWorkout = plan.meals.find(m => m.type === 'pre-workout');
                  const postWorkout = plan.meals.find(m => m.type === 'post-workout');
                  const snack = plan.meals.find(m => m.type === 'snack');
                  const dinner = plan.meals.find(m => m.type === 'dinner');
                  const extras = plan.meals.filter(m => m.type === 'meal2' || m.type === 'meal3');

                  const renderCell = (meal: typeof breakfast | undefined, extra?: string) => (
                    <td className="px-4 py-3 align-top">
                      {meal ? (
                        <div className="space-y-1">
                          {meal.items.slice(0, 3).map((item, i) => (
                            <div key={i} className="text-xs text-foreground/80">
                              <span className="text-muted-foreground">{item.amount}</span>{' '}
                              {item.name}
                            </div>
                          ))}
                          {meal.items.length > 3 && (
                            <div className="text-xs text-muted-foreground">+{meal.items.length - 3} more</div>
                          )}
                        </div>
                      ) : extra ? (
                        <span className="text-xs text-muted-foreground">{extra}</span>
                      ) : (
                        <span className="text-muted-foreground/40 text-xs">—</span>
                      )}
                    </td>
                  );

                  return (
                    <tr
                      key={plan.day}
                      onClick={() => setSelectedDay(dayIdx)}
                      className={`border-b border-border/50 cursor-pointer transition-colors ${
                        dayIdx === selectedDay ? 'bg-primary/5' : 'hover:bg-muted/20'
                      }`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            dayIdx === selectedDay ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                          }`}>
                            {plan.dayNum}
                          </div>
                          <span className="text-sm font-semibold text-foreground">{plan.day}</span>
                        </div>
                      </td>
                      {renderCell(breakfast)}
                      {renderCell(preWorkout)}
                      {renderCell(postWorkout)}
                      {renderCell(snack)}
                      {renderCell(dinner, extras.length > 0 ? extras.map(e => e.label).join(', ') : undefined)}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-6 text-center text-xs text-muted-foreground">
        <div className="flex items-center justify-center gap-2">
          <span>🏋️</span>
          <span>GYM Coach Weekly Nutrition Plan • Designed for Athletic Performance</span>
          <span>🏋️</span>
        </div>
      </footer>
    </div>
  );
}
